import { Component, OnInit, ViewChild, TemplateRef, AfterViewInit, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { Router } from "@angular/router";
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatDividerModule } from '@angular/material/divider';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';

import { AgGridModule } from 'ag-grid-angular';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

import { CriticalDatesService } from '../../service/entity-services/critical-dates.service';
import { SidenavContentService } from '../../service/sidenav-content.service';
import { PermissionsStoreService } from '../../service/permissions/permissions-store.service';

import { EntityList } from '../../objects/entity-list';
import { CriticalDate } from '../../objects/critical-dates';
import { entityPathFor, hasEntityRoute } from '../../shared/utils/entity-route';

import { NewCriticalDateComponent } from '../../create-new/new-critical-date/new-critical-date.component';
import { CriticalDateActionsDialogComponent } from './critical-date-actions-dialog/critical-date-actions-dialog.component';

import * as criticalDateColumns from '../../../app/shared/entity-columns/important-dates-columns';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-critical-dates',
  templateUrl: './critical-dates.component.html',
  styleUrls: ['./critical-dates.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    AgGridModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatGridListModule,
    MatDividerModule,
    MatSidenavModule,
    MatProgressSpinner,
    MatAutocompleteModule,
    MatChipsModule,
    MatIconModule
  ]
})
export class CriticalDatesComponent extends EntityList implements OnInit, AfterViewInit, OnDestroy {
  private router = inject(Router);
  public criticalDatesService = inject(CriticalDatesService);
  private sidenavContentService = inject(SidenavContentService);
  private permissionsStore = inject(PermissionsStoreService);
  private cdr = inject(ChangeDetectorRef);
  // snackBar and dialog are inherited from Entity base class

  @ViewChild('criticalDatesSidenavActions') criticalDatesSidenavActions!: TemplateRef<any>;

  // Observable for critical dates
  allCriticalDates$: Observable<CriticalDate[]> = this.criticalDatesService.visibleCriticalDates$;

  // UI state
  columnChooserControl = new FormControl();
  customizeColumnsUi = false;
  waitingForServer = false;
  showNameSavedView = false;
  isInitializing = true; // For loading screen

  // Permissions - use signal from permissions store
  isViewer = this.permissionsStore.isViewer;

  // Column configuration
  defaultCols: any[] = [
    criticalDateColumns.colImportantDate,
    criticalDateColumns.colReminderDate,
    criticalDateColumns.colDateType,
    criticalDateColumns.colAssignedTo,
    criticalDateColumns.colAssignedBy,
    criticalDateColumns.colEntityName,
    criticalDateColumns.colEntityType,
    criticalDateColumns.colEntityLink
  ];

  selectedCols = this.defaultCols;

  // Column customization properties
  availableColumns: any[] = [];
  availableColumnSelectList: {value: any, viewValue: string}[] = [];
  columnSelectorControl = new FormControl<string>('');
  selectedColumnsList: any[] = [];
  filteredColumns$!: Observable<{value: any, viewValue: string}[]>;

  // AG-Grid default column configuration
  public defaultColDef = {
    filter: true,
    floatingFilter: false,
    resizable: true,
    sortable: true,
    menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'],
  };
  public showFloatingFilters = false;

  // AG Grid configuration
  public frameworkComponents = {};

  constructor() {
    super();
    this.entityType = 'critical-dates'; // Set the entity type for view management
    this.buildAvailableColumns();
    this.initializeSessionStorage();
  }

  async ngOnInit(): Promise<void> {
    // Service will automatically load critical dates via visibleCriticalDates$ observable

    this.titleService.setTitle('Important Dates');

    // NOTE: View loading is now deferred to after grid is ready (see completeInitialization)

    // Set up autocomplete filtering for column selector
    this.filteredColumns$ = this.columnSelectorControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filterColumns(value || ''))
    );
  }

  /**
   * Override onGridReady to complete initialization after grid is ready
   */
  override onGridReady(params: any): void {
    // Call parent class onGridReady first
    super.onGridReady(params);

    // Complete initialization behind the loading screen
    this.completeInitialization().catch(err => {
      console.error('[onGridReady] Error during initialization:', err);
      this.isInitializing = false; // Hide loading screen on error
    });
  }

  /**
   * Completes the grid initialization after grid is ready.
   * Uses official AG Grid APIs for state management.
   * Priority: Session Storage > Saved Views > Auto-size
   */
  private async completeInitialization(): Promise<void> {
    // CRITICAL: Suspend session tracking during initialization
    this.suspendSessionTracking = true;

    // PRIORITY 1: Check for session storage (temporary column adjustments)
    const hasSessionState = this.sessionStorageKey &&
      sessionStorage.getItem(this.sessionStorageKey);

    if (hasSessionState) {
      this.loadSessionState();
      this.suspendSessionTracking = false;
    } else {
      // PRIORITY 2: Load saved views from backend
      await this.loadUserViews();

      const hasNewFormatView = this.savedNamedViews && (this.savedNamedViews as any).gridState;
      const hasOldFormatView = this.savedNamedViews &&
        ((this.savedNamedViews as any).columnState || (this.savedNamedViews as any).savedViewData);

      if (hasNewFormatView || hasOldFormatView) {
        await this.loadNamedView(this.savedNamedViews);
        this.suspendSessionTracking = false;
      } else {
        // PRIORITY 3: No session, no saved view - auto-size
        this.autoSizeAll();
        await new Promise(resolve => setTimeout(resolve, 1100));
      }
    }

    // Hide loading screen - must trigger change detection since we're outside Angular's zone
    this.isInitializing = false;
    this.cdr.detectChanges();
  }

  /**
   * Builds the list of available columns
   */
  private buildAvailableColumns(): void {
    // Start with all Critical Date columns
    this.availableColumns = [...criticalDateColumns.allColumns];

    // Filter out non-column objects
    this.availableColumns = this.availableColumns.filter(col => col && col.colId && col.headerName);

    // "Repeats" is meaningless where the feature is off — no record can carry a
    // recurrence, so this would offer an always-empty column to choose.
    if ((environment as any).recurringImportantDates !== true) {
      this.availableColumns = this.availableColumns.filter(col => col.colId !== 'recurrence');
    }

    // Remove duplicates by colId
    const uniqueColsMap = new Map();
    this.availableColumns.forEach(col => {
      if (!uniqueColsMap.has(col.colId)) {
        uniqueColsMap.set(col.colId, col);
      }
    });
    this.availableColumns = Array.from(uniqueColsMap.values());

    // Build and sort select list
    this.availableColumnSelectList = this.availableColumns.map(col => ({
      value: col,
      viewValue: col.headerName
    }));
    this.availableColumnSelectList.sort((a, b) => a.viewValue.localeCompare(b.viewValue));
  }

  /**
   * Filters available columns based on user input
   */
  private _filterColumns(value: string | any): {value: any, viewValue: string}[] {
    if (!value || typeof value !== 'string') {
      return this.availableColumnSelectList;
    }
    const filterValue = value.toLowerCase();
    return this.availableColumnSelectList.filter(option =>
      option.viewValue.toLowerCase().includes(filterValue)
    );
  }

  /**
   * Display function for autocomplete - returns empty string to keep input clear
   */
  displayColumnFn(): string {
    return '';
  }

  ngAfterViewInit(): void {
    this.sidenavContentService.updateSidenavContent(this.criticalDatesSidenavActions);
  }

  ngOnDestroy(): void {
    this.sidenavContentService.updateSidenavContent(null);
  }

  // Navigation
  createNewCriticalDate(): void {
    this.router.navigateByUrl('/securehome/new-critical-date');
  }

  // Grid events
  onCellClicked(event: any): void {
    console.log('Critical date clicked:', event.data);

    // Open modal dialog with Edit Date and Go To Item buttons
    const dialogRef = this.dialog.open(CriticalDateActionsDialogComponent, {
      width: '400px',
      data: {
        criticalDate: event.data,
        // Resolve rather than trust the stored entityLink: it is written once at
        // create time and is blank on every record saved before the entity-type
        // map was fixed, which hid this button for Party- and Site-attached dates.
        hasEntityLink: hasEntityRoute(event.data?.entityType, event.data?.entityNum || event.data?.entityId)
                       || !!event.data?.entityLink
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'edit') {
        this.openEditDialog(event.data);
      } else if (result === 'goto') {
        const href = entityPathFor(event.data?.entityType, event.data?.entityNum || event.data?.entityId)
                     || event.data?.entityLink;
        if (href) {
          window.open(href, '_blank');
        }
      }
    });
  }

  /**
   * Opens the edit dialog for a critical date
   */
  private openEditDialog(criticalDate: CriticalDate): void {
    const dialogRef = this.dialog.open(NewCriticalDateComponent, {
      width: '800px',
      height: '600px',
      data: {
        title: 'Edit Important Date',
        message: 'Update the important date information',
        criticalDateData: { criticalDateData: criticalDate },
        entityObject: null
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        console.log('Critical date updated:', result);
        // Service automatically refreshes after update
      }
    });
  }

  onSearchChange(searchText: string): void {
    if (this.gridApi) {
      this.gridApi.setGridOption('quickFilterText', searchText);
    }
  }

  // Column management methods
  /**
   * Adds a column to the selected columns list
   */
  selectColumn(column: any): void {
    if (column && !this.selectedColumnsList.find(c => c.colId === column.colId)) {
      this.selectedColumnsList.push(column);
      setTimeout(() => {
        this.columnSelectorControl.setValue('', { emitEvent: false });
      }, 0);
    }
  }

  /**
   * Removes a column from the selected columns list
   */
  removeColumn(column: any): void {
    const index = this.selectedColumnsList.findIndex(c => c.colId === column.colId);
    if (index >= 0) {
      this.selectedColumnsList.splice(index, 1);
    }
  }

  /**
   * Applies the selected columns to the grid
   */
  applyColumns(): void {
    if (this.gridApi && this.selectedColumnsList.length > 0) {
      this.selectedCols = this.selectedColumnsList;
      this.gridApi.setGridOption('columnDefs', this.selectedCols);
      this.customizeColumnsUi = false;
      this.markSessionAsModified();
    }
  }

  /**
   * Opens the customize columns dialog
   */
  openCustomizeColumnsDialog(): void {
    if (this.gridApi) {
      const currentState = this.gridApi.getColumnState();
      const visibleColIds = currentState
        .filter((col: any) => !col.hide)
        .map((col: any) => col.colId);

      this.selectedColumnsList = this.availableColumns.filter(col =>
        visibleColIds.includes(col.colId)
      );
    } else {
      this.selectedColumnsList = [...this.selectedCols];
    }

    this.customizeColumnsUi = true;
  }

  /**
   * Toggles the floating filter row on/off
   */
  toggleFloatingFilters(): void {
    this.showFloatingFilters = !this.showFloatingFilters;

    if (this.gridApi) {
      const allColumnIds: string[] = [];
      this.gridApi.getColumns()?.forEach((column: any) => {
        allColumnIds.push(column.getId());
      });

      allColumnIds.forEach(colId => {
        this.gridApi.setColumnFilterProperty(colId, 'floatingFilter', this.showFloatingFilters);
      });

      this.gridApi.refreshHeader();
    }
  }

  // View management methods (saveView, saveNamedViewNow, loadNamedView,
  // openSavedReportsDialog, openDeleteSavedViewsDialog, showSaveNamedView)
  // are inherited from EntityList base class

  // Export functions with date formatting
  exportCSV(): void {
    if (this.gridApi) {
      const params = {
        processCellCallback: this.formattingFunction,
        fileName: 'critical_dates_export.csv'
      };
      this.gridApi.exportDataAsCsv(params);
    }
  }

  exportExcel(): void {
    if (this.gridApi) {
      const excelParams = {
        processCellCallback: this.formattingFunction,
        fileName: 'critical_dates_export.xlsx'
      };
      this.gridApi.exportDataAsExcel(excelParams);
    }
  }
}
